// staySession — client-side storage of a claimed stay (« Ma Clef du Manoir »,
// PRODUCT_STRATEGY §1.3 / IA audit W5).
//
// A guest who claims a stay code receives {token, suite_id, guest_name,
// checkout} from POST /api/medard/stay/claim. This module persists that
// session in localStorage and answers ONE question for the UI: « is there an
// active stay on this device? ».
//
// Pure & hermetic on purpose:
//   • the clock is injected (`now`) so expiry is testable without timers;
//   • the storage is injected so tests never touch the real localStorage;
//   • load() never throws — corrupt JSON, missing storage (Safari private
//     mode) or an expired session all collapse to null.
//
// Expiry mirrors the backend exactly (stay_service.GRACE_HOURS): the session
// dies at checkout + 24h — the guest token expires server-side at the same
// moment, so we never keep a corpse the backend would reject anyway.

export const STAY_STORAGE_KEY = 'medard-stay';

// Fired on this tab whenever the stay is claimed or ended, so any component
// (the Manoir tab's gate, a badge) can react without prop-drilling. Same-tab
// `storage` events do not fire, hence our own event.
export const STAY_EVENT = 'medard-stay-change';

function emitStayChange() {
    try {
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
            window.dispatchEvent(new Event(STAY_EVENT));
        }
    } catch {
        /* SSR / jsdom without Event — callers still get their return value */
    }
}

// Same grace as backend/stay_service.py — keep the two in lockstep.
export const STAY_GRACE_HOURS = 24;

const GRACE_MS = STAY_GRACE_HOURS * 60 * 60 * 1000;

/** Default storage — guarded: Safari private mode may throw on access. */
function defaultStorage() {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

/** Millisecond timestamp of `now` (Date | number | undefined → Date.now()). */
function toMs(now) {
    if (now instanceof Date) return now.getTime();
    if (Number.isFinite(now)) return now;
    return Date.now();
}

/**
 * True while the session is live: shaped like a claim payload AND before
 * checkout + 24h grace. Pure — no I/O, clock injected.
 */
export function isStayActive(session, now = undefined) {
    if (!session || typeof session !== 'object') return false;
    if (!session.token || !session.suite_id || !session.checkout) return false;
    const checkout = Date.parse(session.checkout);
    if (!Number.isFinite(checkout)) return false;
    return toMs(now) < checkout + GRACE_MS;
}

/**
 * Persist a freshly claimed stay. Returns the stored session (with
 * `claimed_at`) or null when the payload is not a valid claim response or
 * the storage is unavailable — callers can degrade to in-memory state.
 */
export function saveStaySession(payload, { now, storage = defaultStorage() } = {}) {
    if (!payload || typeof payload !== 'object') return null;
    const session = {
        token: payload.token || '',
        suite_id: payload.suite_id || '',
        guest_name: payload.guest_name || '',
        checkout: payload.checkout || '',
        claimed_at: new Date(toMs(now)).toISOString(),
        // A stay posted by the PMS holds the surname alone — the pill then
        // shows it whole (GuestPill.shortGuestName).
        ...(payload.guest_name_is_surname === true ? { guest_name_is_surname: true } : {}),
    };
    if (!session.token || !session.suite_id || !session.checkout) return null;
    try {
        storage?.setItem(STAY_STORAGE_KEY, JSON.stringify(session));
    } catch {
        /* quota / private mode — the caller keeps the in-memory session */
    }
    emitStayChange();
    return session;
}

/**
 * The active stay session stored on this device, or null. An expired or
 * corrupt entry is removed on read (self-cleaning: no stale « Ma Clef »
 * card past checkout + 24h). Never throws.
 */
export function loadStaySession({ now, storage = defaultStorage() } = {}) {
    let raw = null;
    try {
        raw = storage?.getItem(STAY_STORAGE_KEY) ?? null;
    } catch {
        return null;
    }
    if (!raw) return null;
    let session = null;
    try {
        session = JSON.parse(raw);
    } catch {
        session = null;
    }
    if (!isStayActive(session, now)) {
        clearStaySession({ storage });
        return null;
    }
    return session;
}

/** Forget the stay (« Terminer le séjour », or self-cleaning on expiry). */
export function clearStaySession({ storage = defaultStorage() } = {}) {
    try {
        storage?.removeItem(STAY_STORAGE_KEY);
    } catch {
        /* noop */
    }
    emitStayChange();
}
